import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaFileInvoice } from 'react-icons/fa';

const Contact = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      label: t.contact.address,
      value: t.contact.addressValue,
      color: 'from-red-400 to-pink-500',
    },
    {
      icon: FaFileInvoice,
      label: t.contact.vat,
      value: '4021020541450',
      color: 'from-blue-400 to-indigo-500',
    },
    {
      icon: FaPhone,
      label: t.contact.phone,
      value: '+389 71 204 333',
      color: 'from-green-400 to-teal-500',
      link: 'tel:+38971204333',
    },
    {
      icon: FaEnvelope,
      label: t.contact.email,
      value: 'pienergi@hotmail.com',
      color: 'from-orange-400 to-red-500',
      link: 'mailto:pienergi@hotmail.com',
    },
  ];

  return (
    <section
      id="contact"
      ref={ref}
      className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-solar-200/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-energy-200/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gradient" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-solar-400 to-energy-500 mx-auto rounded-full" />
        </motion.div>

        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.03, y: -5 }}
                className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
                data-testid={`contact-info-${index}`}
              >
                <div className="flex items-start space-x-4">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                    className="flex-shrink-0"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <info.icon className="text-white text-2xl" />
                    </div>
                  </motion.div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-500 mb-2">{info.label}</p>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-lg font-medium text-gray-800 hover:text-solar-600 transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-lg font-medium text-gray-800">{info.value}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Map placeholder with image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 rounded-2xl overflow-hidden shadow-2xl"
          >
            <div className="relative h-96">
              <img
                src="/images/contact.jpg"
                alt="Solar Installation"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <h3 className="text-3xl font-bold mb-2">PI ENERGI DOOEL Resen</h3>
                <p className="text-lg opacity-90">Goce Delecev no. 3, Resen, North Macedonia</p>
              </div>
              
              {/* Animated pulse on location */}
              <motion.div
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.7, 0, 0.7],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute top-1/2 left-1/2 w-4 h-4 bg-red-500 rounded-full transform -translate-x-1/2 -translate-y-1/2"
              />
              <div className="absolute top-1/2 left-1/2 w-4 h-4 bg-red-500 rounded-full transform -translate-x-1/2 -translate-y-1/2 shadow-lg" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;